import React from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faCamera,
  faTasks,
  faSearch,
  faSortAlphaUp,
  faClock,
  faUsers,
  faPeopleCarry,
  faCog,
  faTimes,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import CreateKeyPopup from "../../components/CreateKeyPopup";
import ScreenshotPreview from "../../components/ScreenshotPreview";
import ScreenshotsFilter from "../../components/ScreenshotsFilter";
import Languages from "../../components/Languages";
import { Error } from "../../components/Error";
import { ProjectKeys } from "../../components/ProjectKeys";
import EditProjectMembersPopup from "../../components/EditProjectMembersPopup";
import EditProjectPopup from "../../components/EditProjectPopup";
import { PurgeButton } from "../../components/PurgeButton";
import { useProject } from "../../queries/use-project";
import { VersionsDropDown } from "../../components/VersionsDropDown";
import { LanguageProvider } from "../../context/LanguageContext";
import EditProjectContributorsPopup from "../../components/EditProjectContributorsPopup";
import {
  PermissionProvider,
  usePermission,
} from "../../context/PermissionContext";
import { ExportButton } from "../../components/ExportButton";
import { ImportButton } from "../../components/ImportButton";
import { SyncOneskyButton } from "../../components/SyncOneskyButton";

const HeaderIconButton = ({ icon, label, onClick }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    className="inline-flex items-center justify-center w-9 h-9 rounded-full text-gray-500 bg-white border border-gray-200 hover:text-primary hover:border-primary transition"
  >
    <FontAwesomeIcon icon={icon} className="w-4 h-4" />
  </button>
);

const Chip = ({
  active,
  onClick,
  icon,
  children,
  activeClass = "bg-primary text-white border-primary",
}) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      "inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-sm font-medium transition whitespace-nowrap",
      active
        ? activeClass
        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-800"
    )}
  >
    {icon && <FontAwesomeIcon icon={icon} className="w-3.5 h-3.5" />}
    {children}
  </button>
);

const ProjectHeader = ({
  project,
  setShowEditMembersForm,
  setShowContributorsForm,
  setShowEditForm,
  versionId,
  setVersionId,
}) => {
  const { hasAdminRight } = usePermission();
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
      <div className="min-w-0">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 truncate">
          {project.name}
        </h1>
        <p className="mt-1 text-sm text-gray-500">Project #{project.id}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {hasAdminRight && (
          <>
            <HeaderIconButton
              icon={faUsers}
              label="Members"
              onClick={() => setShowEditMembersForm(true)}
            />
            <HeaderIconButton
              icon={faPeopleCarry}
              label="Contributors"
              onClick={() => setShowContributorsForm(true)}
            />
            <HeaderIconButton
              icon={faCog}
              label="Project settings"
              onClick={() => setShowEditForm(true)}
            />
          </>
        )}
        <VersionsDropDown
          projectId={project.id}
          versionId={versionId}
          setVersionId={setVersionId}
        />
      </div>
    </div>
  );
};

const Toolbar = ({
  project,
  versionId,
  searchName,
  setSearchName,
  filter,
  setFilter,
  sort,
  setSort,
  setSelectedFilterScreenshot,
  setShowCreateForm,
}) => {
  const anyFilterActive = Boolean(filter);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex flex-col gap-3 mb-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 min-w-0">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400 text-sm">
            <FontAwesomeIcon icon={faSearch} />
          </span>
          <input
            type="text"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            placeholder="Search keys or translations…"
            className="pl-9 pr-9 h-9 w-full rounded-full border border-gray-200 bg-white text-sm leading-none placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition"
          />
          {searchName && (
            <button
              type="button"
              onClick={() => setSearchName("")}
              aria-label="Clear search"
              className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <FontAwesomeIcon icon={faTimes} className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap lg:justify-end lg:ml-auto">
          {Boolean(project.oneskyId) && (
            <SyncOneskyButton projectId={project.id} version={versionId} />
          )}
          <ImportButton projectId={project.id} version={versionId} />
          <ExportButton projectId={project.id} version={versionId} />
          <PurgeButton projectId={project.id} version={versionId} />
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-white text-sm font-semibold px-4 h-9 shadow-sm hover:opacity-95 transition"
            onClick={() => setShowCreateForm(true)}
          >
            <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5" />
            Add key
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100 mt-1">
        <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold mr-1 flex items-center gap-1.5">
          <FontAwesomeIcon icon={faFilter} className="w-3 h-3" />
          Filter
        </span>
        <Chip
          active={filter === "untranslate"}
          onClick={() =>
            setFilter(filter === "untranslate" ? null : "untranslate")
          }
          icon={faTasks}
        >
          Untranslated
        </Chip>
        <Chip
          active={filter === "screenshot"}
          onClick={() => {
            setFilter(filter === "screenshot" ? null : "screenshot");
            setSelectedFilterScreenshot(null);
          }}
          icon={faCamera}
        >
          Screenshot
        </Chip>
        {anyFilterActive && (
          <button
            type="button"
            className="ml-1 text-xs text-gray-500 hover:text-gray-800 underline"
            onClick={() => {
              setFilter(null);
              setSelectedFilterScreenshot(null);
            }}
          >
            Clear filters
          </button>
        )}

        <div className="mx-2 h-5 w-px bg-gray-200 hidden sm:block" />

        <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold mr-1">
          Sort
        </span>
        <Chip
          active={sort === "name"}
          onClick={() => setSort("name")}
          icon={faSortAlphaUp}
        >
          Name
        </Chip>
        <Chip
          active={sort === "time"}
          onClick={() => setSort("time")}
          icon={faClock}
        >
          Recent
        </Chip>
      </div>
    </div>
  );
};

const Project = () => {
  const { id } = useParams();
  const [selectedFilterScreenshot, setSelectedFilterScreenshot] =
    React.useState(null);
  const [versionId, setVersionId] = React.useState(null);
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const [showContributorsForm, setShowContributorsForm] = React.useState(false);
  const [showEditForm, setShowEditForm] = React.useState(false);
  const [showEditMembersForm, setShowEditMembersForm] = React.useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = React.useState(null);
  const [filter, setFilter] = React.useState(null);
  const [sort, setSort] = React.useState("name");
  const [searchName, setSearchName] = React.useState("");
  const { data: project, isLoading, isError } = useProject(id);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-gray-400 text-sm">
        Loading project…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Error />
      </div>
    );
  }

  return (
    <PermissionProvider projectId={project.id}>
      <LanguageProvider project={project}>
        <ProjectHeader
          project={project}
          setShowEditMembersForm={setShowEditMembersForm}
          setShowContributorsForm={setShowContributorsForm}
          setShowEditForm={setShowEditForm}
          versionId={versionId}
          setVersionId={setVersionId}
        />

        <Toolbar
          project={project}
          versionId={versionId}
          searchName={searchName}
          setSearchName={setSearchName}
          filter={filter}
          setFilter={setFilter}
          sort={sort}
          setSort={setSort}
          setSelectedFilterScreenshot={setSelectedFilterScreenshot}
          setShowCreateForm={setShowCreateForm}
        />

        <div className="flex flex-col flex-1 min-h-0">
          {filter === "screenshot" && (
            <ScreenshotsFilter
              selectedId={selectedFilterScreenshot}
              onChange={(id) => setSelectedFilterScreenshot(id)}
            />
          )}
          <Languages projectId={id} />
          <ProjectKeys
            project={project}
            selectedFilterScreenshot={selectedFilterScreenshot}
            filter={filter}
            sort={sort}
            setSelectedScreenshot={setSelectedScreenshot}
            version={versionId}
            searchName={searchName}
            setSearchName={setSearchName}
          />
        </div>

        {showCreateForm && (
          <CreateKeyPopup
            projectId={id}
            version={versionId}
            baseLanguage={project.baseLanguageId}
            onClose={() => setShowCreateForm(false)}
          />
        )}
        {showEditMembersForm && (
          <EditProjectMembersPopup
            projectId={id}
            baseLanguage={project.baseLanguageId}
            onClose={() => setShowEditMembersForm(false)}
          />
        )}
        {showContributorsForm && (
          <EditProjectContributorsPopup
            projectId={id}
            baseLanguage={project.baseLanguageId}
            onClose={() => setShowContributorsForm(false)}
          />
        )}
        {showEditForm && (
          <EditProjectPopup
            projectId={id}
            onClose={() => setShowEditForm(false)}
          />
        )}
        {selectedScreenshot && (
          <ScreenshotPreview
            screenshot={selectedScreenshot}
            onClose={() => setSelectedScreenshot(null)}
          />
        )}
      </LanguageProvider>
    </PermissionProvider>
  );
};

export default Project;
